import { useEffect, useState } from 'react';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import { Notice } from '@/components/ui/Notice';
import { EmailLink, TextLink } from '@/components/ui/TextLink';
import { Pager } from '@/components/marketing/lists/ListMembersTable';
import { useDebounced } from '@/components/marketing/lists/ref-options';
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  Modal,
  MultiCombobox,
  Panel,
  PanelHeader,
  Skeleton,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  TableWrap,
  Textarea,
} from '@/components/ui';
import { ApiError, api } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { SUPPRESSION_REASON_LABEL } from '@/lib/marketing';
import type { MarketingSuppression, MarketingSuppressionReason } from '@/lib/types';
import { useAuth } from '@/lib/use-auth';

/*
 * Marketing → Settings → Suppressions. `GET /marketing/suppressions`.
 *
 * A suppressed address is skipped at send time by every campaign. Adding one by
 * hand writes reason `manual`; removing one does NOT clear the email opt-out
 * flag on any CRM contact — the confirmation says so, because the two are
 * separate facts and the obvious reading is that removing a suppression lets
 * mail flow again.
 */

const MODULE = 'marketing-settings' as const;
const PER_PAGE = 25;
const SKELETON_ROWS = 4;
const REASONS = Object.keys(SUPPRESSION_REASON_LABEL) as MarketingSuppressionReason[];
const TABLE_STYLE = { minWidth: `${260 + 144 + 200 + 220 + 200 + 64}px` };

const REASON_TONE: Record<MarketingSuppressionReason, 'neutral' | 'warning' | 'danger'> = {
  unsubscribed: 'neutral',
  hard_bounce: 'danger',
  complaint: 'danger',
  manual: 'warning',
};

const describe = (caught: unknown, fallback: string) =>
  caught instanceof ApiError ? (caught.formMessage ?? caught.message ?? fallback) : fallback;

function SourceLink({ row }: { row: MarketingSuppression }) {
  if (!row.sourceCampaign) return <span className="text-xs text-fg-subtle">—</span>;
  return (
    <TextLink to={`/marketing/campaigns/${row.sourceCampaign.id}/report`} className="text-sm [overflow-wrap:anywhere]">
      {row.sourceCampaign.name}
    </TextLink>
  );
}

function AddedCell({ row }: { row: MarketingSuppression }) {
  return (
    <div className="grid gap-0.5">
      <span className="text-xs text-fg-secondary [overflow-wrap:anywhere]">
        {row.createdBy ? row.createdBy.name || row.createdBy.email : 'SEED EMERGE'}
      </span>
      <span className="text-xs text-fg-muted tabular">{formatDateTime(row.createdAt)}</span>
    </div>
  );
}

export function SuppressionsTable() {
  const { ability } = useAuth();
  const canUpdate = ability.canUpdate(MODULE);

  const [search, setSearch] = useRememberedState('suppressions-search', '');
  const debounced = useDebounced(search.trim());
  // Several reasons at once (owner 2026-10-28): a comma-separated list, '' =
  // all. A single reason remembered before reads back as a one-item selection.
  const [reason, setReason] = useRememberedState<string>('suppressions-reason', '', (raw) =>
    typeof raw === 'string' &&
    raw.split(',').every((part) => part === '' || (REASONS as readonly string[]).includes(part))
      ? raw
      : undefined,
  );
  const reasons = reason.split(',').filter((part) => part !== '');
  const [page, setPage] = useState(1);
  const filterKey = `${debounced}|${reason}`;
  const [lastFilterKey, setLastFilterKey] = useState(filterKey);
  if (lastFilterKey !== filterKey) {
    setLastFilterKey(filterKey);
    setPage(1);
  }

  const [rows, setRows] = useState<MarketingSuppression[] | null>(null);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1 });
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  /* The request a settled answer belongs to — loading is "the current key has no answer yet". */
  const requestKey = `${page}|${filterKey}|${reload}`;
  const [settledKey, setSettledKey] = useState<string | null>(null);
  const loading = settledKey !== requestKey;

  const [addOpen, setAddOpen] = useState(false);
  const [addEmail, setAddEmail] = useState('');
  const [addNote, setAddNote] = useState('');
  const [adding, setAdding] = useState(false);
  const [addErrors, setAddErrors] = useState<Record<string, string>>({});
  const [addError, setAddError] = useState<string | null>(null);

  const [removing, setRemoving] = useState<MarketingSuppression | null>(null);
  const [removeBusy, setRemoveBusy] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    api
      .listMarketingSuppressions(
        {
          page,
          perPage: PER_PAGE,
          ...(debounced ? { search: debounced } : {}),
          ...(reason ? { reason } : {}),
        },
        controller.signal,
      )
      .then((response) => {
        setRows(response.data);
        setMeta({ total: response.meta.total, totalPages: response.meta.totalPages });
        setError(null);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setRows(null);
        setError(
          caught instanceof ApiError && caught.status === 403
            ? 'Your account is not allowed to read suppressions.'
            : describe(caught, 'Suppressions could not be loaded.'),
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setSettledKey(`${page}|${debounced}|${reason}|${reload}`);
      });
    return () => controller.abort();
  }, [page, debounced, reason, reload]);

  function openAdd() {
    setAddEmail('');
    setAddNote('');
    setAddErrors({});
    setAddError(null);
    setAddOpen(true);
  }

  async function submitAdd() {
    const email = addEmail.trim();
    if (email === '') {
      setAddErrors({ email: 'Enter an email address.' });
      return;
    }
    setAdding(true);
    setAddErrors({});
    setAddError(null);
    try {
      await api.createMarketingSuppression({ email, note: addNote.trim() === '' ? null : addNote.trim() });
      setAddOpen(false);
      setReload((n) => n + 1);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status !== 403) {
        const { fields, unroutable } = caught.routeFieldErrors(['email', 'note']);
        setAddErrors(fields);
        setAddError(unroutable.length > 0 ? unroutable.join(' ') : Object.keys(fields).length > 0 ? null : caught.message);
      } else if (caught instanceof ApiError) {
        setAddError(caught.message || 'Your account is not allowed to add suppressions.');
      } else {
        setAddError('Could not reach the server. Nothing was added.');
      }
    } finally {
      setAdding(false);
    }
  }

  async function confirmRemove() {
    if (!removing) return;
    setRemoveBusy(true);
    setRemoveError(null);
    try {
      await api.deleteMarketingSuppression(removing.id);
      setRemoving(null);
      setReload((n) => n + 1);
    } catch (caught) {
      setRemoveError(describe(caught, 'Could not reach the server. The suppression has NOT been removed.'));
    } finally {
      setRemoveBusy(false);
    }
  }

  const list = rows ?? [];
  const initialLoad = loading && rows === null && error === null;
  const filtered = debounced !== '' || reason !== '';

  return (
    <Panel className="min-w-0" aria-labelledby="marketing-suppressions-heading">
      <PanelHeader
        id="marketing-suppressions-heading"
        title="Suppressions"
        description={rows === null ? undefined : `${meta.total} ${meta.total === 1 ? 'address' : 'addresses'}`}
        actions={
          canUpdate ? (
            <Button variant="primary" size="sm" onClick={openAdd}>
              Add suppression
            </Button>
          ) : undefined
        }
      />

      <div className="flex flex-wrap items-end gap-3 border-b border-border px-4 py-3">
        <div className="min-w-0 flex-1 basis-56">
          <Field label="Search" hideLabel>
            {(props) => (
              <Input
                {...props}
                type="search"
                placeholder="Search email"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            )}
          </Field>
        </div>
        <div className="w-full sm:w-52">
          <Field label="Reason" hideLabel>
            {(props) => (
              <MultiCombobox
                id={props.id}
                values={reasons}
                onChange={(values) => setReason(values.join(','))}
                options={REASONS.map((r) => ({ value: r, label: SUPPRESSION_REASON_LABEL[r] }))}
                placeholder="All reasons"
              />
            )}
          </Field>
        </div>
      </div>

      {error && (
        <div className="border-b border-border px-4 py-3">
          <Notice tone="danger" role="alert" title={<>Couldn&rsquo;t load suppressions.</>}>
            <p className="m-0 [overflow-wrap:anywhere]">{error}</p>
          </Notice>
        </div>
      )}

      <div aria-busy={loading || undefined} className={loading && rows ? 'opacity-60' : undefined}>
        {initialLoad ? (
          <ul className="m-0 grid list-none gap-2 p-4">
            {Array.from({ length: SKELETON_ROWS }, (_, i) => (
              <li key={i} className="grid gap-1.5">
                <Skeleton className="h-3 w-56" />
                <Skeleton className="h-2.5 w-32" />
              </li>
            ))}
          </ul>
        ) : list.length === 0 ? (
          error ? null : (
            <EmptyState
              title={filtered ? 'No suppressions match' : 'No suppressed addresses'}
              description={
                filtered
                  ? 'Clear the search or reason filter to see every suppressed address.'
                  : 'Unsubscribes, hard bounces and complaints are added here automatically.'
              }
            />
          )
        ) : (
          <>
            <div className="hidden md:block">
              <TableWrap frozenHeader>
                <Table style={TABLE_STYLE}>
                  <THead>
                    <TR>
                      <TH>Email</TH>
                      <TH className="w-36">Reason</TH>
                      <TH className="w-52">Source campaign</TH>
                      <TH className="w-56">Note</TH>
                      <TH className="w-52">Added</TH>
                      {canUpdate && <TH className="w-24" numeric>Actions</TH>}
                    </TR>
                  </THead>
                  <TBody>
                    {list.map((row) => (
                      <TR key={row.id}>
                        <TD>
                          <EmailLink address={row.email} className="text-sm" />
                        </TD>
                        <TD>
                          <Badge tone={REASON_TONE[row.reason]}>{SUPPRESSION_REASON_LABEL[row.reason]}</Badge>
                        </TD>
                        <TD>
                          <SourceLink row={row} />
                        </TD>
                        <TD>
                          <span className="text-xs text-fg-secondary [overflow-wrap:anywhere]">{row.note ?? '—'}</span>
                        </TD>
                        <TD>
                          <AddedCell row={row} />
                        </TD>
                        {canUpdate && (
                          <TD numeric>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setRemoveError(null);
                                setRemoving(row);
                              }}
                            >
                              Remove
                            </Button>
                          </TD>
                        )}
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </TableWrap>
            </div>
            <ul className="m-0 grid list-none gap-3 p-4 md:hidden">
              {list.map((row) => (
                <li key={row.id} className="grid min-w-0 gap-2 rounded-md border border-border p-3">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <EmailLink address={row.email} className="min-w-0 text-sm font-medium" />
                    <Badge tone={REASON_TONE[row.reason]}>{SUPPRESSION_REASON_LABEL[row.reason]}</Badge>
                  </div>
                  <dl className="m-0 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-xs">
                    <dt className="text-fg-muted">Campaign</dt>
                    <dd className="m-0">
                      <SourceLink row={row} />
                    </dd>
                    <dt className="text-fg-muted">Note</dt>
                    <dd className="m-0 text-fg-secondary [overflow-wrap:anywhere]">{row.note ?? '—'}</dd>
                    <dt className="text-fg-muted">Added</dt>
                    <dd className="m-0">
                      <AddedCell row={row} />
                    </dd>
                  </dl>
                  {canUpdate && (
                    <div>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          setRemoveError(null);
                          setRemoving(row);
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      {rows !== null && meta.total > 0 && (
        <Pager
          page={page}
          totalPages={meta.totalPages}
          total={meta.total}
          shown={list.length}
          noun={{ singular: 'address', plural: 'addresses' }}
          onPage={setPage}
        />
      )}

      <Modal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        size="sm"
        title="Add a suppression"
        footer={
          <>
            <Button variant="secondary" onClick={() => setAddOpen(false)} disabled={adding}>
              Cancel
            </Button>
            <Button variant="primary" loading={adding} onClick={() => void submitAdd()}>
              Add suppression
            </Button>
          </>
        }
      >
        <form
          className="grid gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void submitAdd();
          }}
        >
          {addError && (
            <Notice tone="danger" role="alert" className="[overflow-wrap:anywhere]">
              {addError}
            </Notice>
          )}
          <Field label="Email address" required error={addErrors.email}>
            {(props) => (
              <Input
                {...props}
                type="email"
                autoComplete="off"
                value={addEmail}
                onChange={(e) => setAddEmail(e.target.value)}
                invalid={Boolean(addErrors.email)}
              />
            )}
          </Field>
          <Field label="Note" error={addErrors.note}>
            {(props) => (
              <Textarea
                {...props}
                rows={2}
                maxLength={500}
                value={addNote}
                onChange={(e) => setAddNote(e.target.value)}
                invalid={Boolean(addErrors.note)}
              />
            )}
          </Field>
          <button type="submit" className="sr-only" tabIndex={-1}>
            Add suppression
          </button>
        </form>
      </Modal>

      <ConfirmDialog
        open={removing !== null}
        onClose={() => {
          setRemoving(null);
          setRemoveError(null);
        }}
        onConfirm={() => void confirmRemove()}
        tone="danger"
        title={removing ? `Remove the suppression on ${removing.email}?` : 'Remove suppression?'}
        description={
          <p className="m-0 [overflow-wrap:anywhere]">
            Campaigns stop skipping this address for being suppressed. This does NOT clear the email opt-out
            flag on any CRM contact with this address — a contact who opted out is still skipped until that
            flag is changed on the contact.
          </p>
        }
        confirmLabel="Remove suppression"
        loading={removeBusy}
        error={removeError && <span className="[overflow-wrap:anywhere]">{removeError}</span>}
      />
    </Panel>
  );
}
